import React, { useState, useRef } from "react";
import {
  Avatar,
  Button,
  TextField,
  Typography,
  Container,
  CssBaseline,
  Card,
  Box,
} from "@mui/material";
import { LockOutlined } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { useMutation } from "@apollo/client";
import { REQUEST_RESET, RESET_PASSWORD, SEND_EMAIL_MUTATION } from "../../graphql/mutations";

const ForgotPassword = () => {
  const navigate = useNavigate();
  const [stage, setStage] = useState("requestReset");
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [verificationCode, setVerificationCode] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [resetToken, setResetToken] = useState("");
  const [error, setError] = useState(null);

  const [requestResetMutation] = useMutation(REQUEST_RESET);
  const [resetPasswordMutation] = useMutation(RESET_PASSWORD);
  const [sendEmailMutation] = useMutation(SEND_EMAIL_MUTATION);

  // Request password reset
  const requestReset = async () => {
    setError(null);
    if (!email.trim()) {
      setError("Email is required.");
      return;
    }
    try {
      const randomCode = Math.floor(100000 + Math.random() * 900000).toString();
      setVerificationCode(randomCode);

      await sendEmailMutation({
        variables: { 
          to: email,
          subject: "Your verification code",
          text: `Your verification code is ${randomCode}`,
        },
      });

      const result = await requestResetMutation({ variables: { email } });
      setResetToken(result.data.requestReset.resetToken);
      setStage("enterCode");
    } catch (err) {
      setError("Failed to send reset email. Please try again.");
    }
  };

  // Verify reset code
  const verifyResetCode = () => {
    if (code !== verificationCode) {
      setError("Incorrect verification code.");
      return;
    }
    setError(null);
    setStage("resetPassword");
  };

  // Reset password
  const resetUserPassword = async () => {
    setError(null);
    if (password !== confirmPassword) {
      setError("Passwords do not match.");
      return;
    }
    try {
      await resetPasswordMutation({
        variables: { email, password, resetToken },
      });
      setStage("success");
    } catch (error) {

      if (error.graphQLErrors && error.graphQLErrors.length > 0) {
        // For example, use the first error message.
        const errorMessage = error.graphQLErrors[0].message;
        console.error("GraphQL error:", errorMessage);
        // Now update your UI state to display the error message.
        setError(errorMessage);
      } else if (error.networkError) {
        console.error("Network error:", error.networkError);
        setError("A network error occurred. Please try again.");
      } else {
        console.error("Unexpected error:", error);
        setError("An unexpected error occurred.");
      } 
    }
  };

  const renderError = () =>
    error && (
      <Typography color="error" align="center" style={{ marginBottom: "10px" }}>
        {error}
      </Typography>
    );

  return (
    <Container component="main" maxWidth="xs">
      <CssBaseline />
      <Card style={{ padding: "30px", marginTop: "50px" }}>
        <Box display="flex" flexDirection="column" alignItems="center">
          <Avatar sx={{ bgcolor: "secondary.main" }}>
            <LockOutlined />
          </Avatar>
          <Typography component="h1" variant="h5">
            {stage === "requestReset"
              ? "Forgot Password"
              : stage === "enterCode"
              ? "Enter Verification Code"
              : stage === "resetPassword"
              ? "Reset Password"
              : "Success"}
          </Typography>
        </Box>
        {renderError()}
        {stage === "requestReset" && (
          <Box component="form" mt={3}>
            <TextField
              fullWidth
              variant="outlined"
              label="Email Address"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
            <Button
              fullWidth
              variant="contained"
              color="primary"
              onClick={requestReset}
              sx={{ mt: 3 }}
            >
              Send Verification Code
            </Button>
          </Box>
        )}
        {stage === "enterCode" && (
          <Box component="form" mt={3}>
            <TextField
              fullWidth
              variant="outlined"
              label="Verification Code"
              value={code}
              onChange={(e) => setCode(e.target.value)}
            />
            <Button
              fullWidth
              variant="contained"
              color="primary"
              onClick={verifyResetCode}
              sx={{ mt: 3 }}
            >
              Verify Code
            </Button>
          </Box>
        )}
        {stage === "resetPassword" && (
          <Box component="form" mt={3}>
            <TextField
              fullWidth
              variant="outlined"
              label="New Password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
            <TextField
              fullWidth
              variant="outlined"
              label="Confirm Password"
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              sx={{ mt: 2 }}
            />
            <Button
              fullWidth
              variant="contained"
              color="primary"
              onClick={resetUserPassword}
              sx={{ mt: 3 }}
            >
              Reset Password
            </Button>
          </Box>
        )}
        {stage === "success" && (
          <Box mt={3} textAlign="center">
            <Typography variant="h6" color="success.main">
              Password Reset Successfully!
            </Typography>
            <Button
              fullWidth
              variant="contained"
              color="primary"
              onClick={() => navigate("/login")}
              sx={{ mt: 3 }}
            >
              Go to Login
            </Button>
          </Box>
        )}
      </Card>
    </Container>
  );
};

export default ForgotPassword;
